import {
  type JSX,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Schliesskreuz } from './Schliesskreuz';
import { istMaus, useSchwebe } from './schwebe';
import { useFeinerZeiger } from './zeiger';

/**
 * Die Aufklapper einer Zeile, die beim Überfahren öffnen (Auftrag #21).
 *
 * Sie hält, welcher gerade offen ist. Ist einer offen, wechselt das Überfahren
 * des nächsten sofort und ohne Absicht — wer schon in der Zeile ist, hat sich
 * entschieden, und 120 ms je Gruppe fühlten sich wie ein Stocken an.
 */
export type Schwebezeile = {
  /**
   * Schließt den offenen Aufklapper der Zeile; `null`, wenn keiner offen ist.
   * Mit `nurSchwebend` nur einen, den die Maus geöffnet hat — ein angehefteter
   * bleibt (Auftrag CRM-49).
   */
  offen: ((nurSchwebend?: boolean) => void) | null;
};

/**
 * EIN PUNKT DER ZEILE OHNE PFEIL (Auftrag CRM-49). Er öffnet nichts, aber wer
 * mit der Maus auf ihn fährt, hat die offene Gruppe verlassen: Sie schließt
 * sofort, wie beim Wechsel auf eine Gruppe mit Pfeil. Bis dahin wartete sie den
 * Nachlauf ab, und so lange stand ihr Pfeil noch auf „offen", obwohl der Zeiger
 * schon daneben war. Der Nachlauf bleibt für den Weg vom Knopf hinunter in die
 * eigene Liste. Eine angeheftete Gruppe bleibt offen; Finger und Stift
 * überfahren nichts (NFA-07).
 */
export function eintrittOhnePfeil(zeile: Schwebezeile, e: Pick<ReactPointerEvent, 'pointerType'>): void {
  if (istMaus(e)) zeile.offen?.(true);
}

/** Ein Eintrag eines Aufklappers. */
export type Aufklappeintrag = {
  readonly text: string;
  /** Eine zweite, ruhige Zeile unter dem Text — der Kunde zur Wiedervorlage (Auftrag CRM-170). */
  readonly unterzeile?: string;
  /** Der Schlüssel der Zeile, wenn der Text nicht eindeutig ist (zwei gleiche Betreffe). */
  readonly schluessel?: string;
  /** Zusatz rechts im Eintrag — eine Zahl, eine Frist. */
  readonly zusatz?: ReactNode;
  /** Ein Sinnbild vor dem Text (Light, wie im Rahmen). */
  readonly vorne?: ReactNode;
  /** Der Eintrag ist die Seite, auf der man steht (`aria-current="page"`). */
  readonly aktuell?: boolean;
  /** Eine Beschriftung für Vorlesehilfen, wenn der Text allein nicht genügt. */
  readonly vorlesen?: string;
  /** Eine Linie trennt ihn von den folgenden Einträgen (ÄA-68). */
  readonly abgesetzt?: boolean;
  /**
   * Der Eintrag fehlt, wo ein feiner Zeiger überfahren kann (Auftrag CRM-148)
   * — es sei denn, ein Finger hat die Liste geöffnet.
   */
  readonly nurFuerFinger?: boolean;
  readonly beiWahl: () => void;
};

/**
 * DER AUFKLAPPER DES ENTWURFS V2 (ÄA-41).
 *
 * Weiß, gerundet wie ein Dialog (6 px, ÄA-44), der Schatten des Entwurfs
 * (`0 12px 32px`), Einträge mit 9/10 px und einer Fläche unter der Maus. Er trägt die Gruppen der
 * Menüzeile, „Neu", die Benachrichtigungen und das Benutzermenü.
 *
 * ============================================================================
 * EIN AUFKLAPPER UND KEIN MENÜ IM SINNE VON ARIA
 * ============================================================================
 *
 * Die Einträge sind Wege zu Masken, keine Befehle einer Anwendung. Das Muster
 * dafür ist die Offenlegung („disclosure"): ein Knopf mit `aria-expanded`, der
 * eine Liste gewöhnlicher Knöpfe zeigt. `role="menu"` versprach einer
 * Vorlesehilfe eine Menüsteuerung, in der Tab nicht mehr durch die Einträge
 * geht — das wäre für eine Navigation die schlechtere Wahl.
 *
 * Die Tastatur trotzdem vollständig (NFA-07, Auftrag #17): Pfeil nach unten
 * öffnet und springt auf den ersten Eintrag, die Pfeile wandern, Pos1 und Ende
 * springen an die Ränder, Escape schließt und gibt den Fokus an den Knopf
 * zurück. Wer mit Tab hinausgeht oder daneben klickt, schließt ihn auch.
 *
 * DIE LISTE STEHT FEST UND NICHT IM FLUSS. Die Menüzeile rollt waagerecht,
 * wenn das Fenster schmal ist; eine Liste darin würde von ihr abgeschnitten.
 * Sie nimmt deshalb die Stelle unter ihrem Knopf und liegt darüber.
 *
 * IN DER MENÜZEILE ÖFFNET ER BEIM ÜBERFAHREN (Auftrag #21, ÄA-41). Mit `zeile`
 * öffnet die Maus ihn nach einer kurzen Absicht und schließt ihn mit Nachlauf
 * (`schwebe.ts`). Die Liste liegt im DOM in der Hülle; der Weg vom Knopf in die
 * Liste verlässt die Hülle deshalb nur über die Lücke dazwischen, und die
 * überbrückt der Nachlauf. Fährt die Maus dagegen auf einen anderen Punkt der
 * Zeile, schließt er sofort: Eine Gruppe mit Pfeil löst ihn ab, ein Punkt ohne
 * Pfeil schließt ihn (`eintrittOhnePfeil`, Auftrag CRM-49). Ein Klick auf einen
 * so geöffneten Aufklapper heftet ihn an, statt ihn wieder zu schließen: Wer
 * klickt, will die Liste haben. Ein angehefteter bleibt, bis man wählt,
 * daneben klickt oder Escape drückt; nur eine andere Gruppe, die die Maus
 * öffnet, löst auch ihn ab. Tastatur und Tippen gehen wie ohne `zeile`
 * (NFA-07). „Neu", Glocke und Benutzermenü bekommen keine `zeile` und öffnen
 * nur beim Klick.
 *
 * ============================================================================
 * MIT `verweis`: EIN GETEILTER EINTRAG (Auftrag #25)
 * ============================================================================
 *
 * Auftraggeber (29.09.2026): „Entferne die Übersicht und mache den Menüpunkt
 * klickbar zur Übersichtsseite." Die Gruppe ist dann ZWEI Elemente und nicht
 * eines mit zwei Bedeutungen (WCAG 4.1.2, NFA-14):
 *
 *   - ein VERWEIS (`<a href>`) auf die Übersichtsseite der Gruppe. Er ist, was
 *     er sagt: ein Weg zu einer Seite — mit mittlerer Maustaste, „in neuem
 *     Tab öffnen" und `aria-current`. Pfeil nach unten auf ihm öffnet die
 *     Liste und springt hinein (NFA-07, WCAG 2.1.1).
 *   - ein kleiner KNOPF mit dem Pfeil (`aria-expanded`) für die Liste. Er ist
 *     der Weg für alle, die mit Tab gehen und die Liste wollen, ohne die
 *     Seite zu wechseln — bis ÄA-68 auch für das Tippen (siehe unten).
 *
 * Das Überfahren gilt der ganzen Hülle und öffnet wie seit #21.
 *
 * SEIT ÄA-68 IST DER PFEILKNOPF IN DER MENÜZEILE UNSICHTBAR, solange er keinen
 * Tastaturfokus hat (stil.css), und er fängt keinen Zeiger. Den Weg für das
 * Tippen übernimmt der Verweis: Ein Finger oder Stift schaltet auf ihm die
 * Liste um wie zuvor auf dem Pfeil, statt wegzuführen. Die Übersichtsseite
 * erreicht er über den ersten Eintrag der Liste, „Übersicht" (Rahmen.tsx) —
 * wie in der Schublade des Telefons, wo ein Tippen, das zugleich aufklappt
 * und wegführt, ebenso ein Fehlgriff wäre. Maus und Tastatur folgen dem
 * Verweis wie bisher.
 *
 * SEIT AUFTRAG CRM-148 FEHLT „ÜBERSICHT" AM RECHNER (Nutzer, 09.10.2026: „Nimm
 * im crm in der desktopansicht die Menüpunkte Übersicht oben aus dem Menü, da
 * man hierfür direkt auf den Eintrag in der leiste klicken kann."). Ein
 * Eintrag mit `nurFuerFinger` steht nicht da, wenn das Gerät einen feinen
 * Zeiger mit Überfahren hat (`zeiger.ts`) — außer ein Finger hat die Liste
 * geöffnet. Das hält den Weg zur Übersichtsseite auf einem Rechner mit
 * Berührschirm, dessen Medienabfrage nur die Maus meldet. Gemerkt wird das
 * beim Öffnen; ein Tippen, das eine überfahrene Liste nur anheftet, lässt sie
 * stehen, wie sie ist, damit sie nicht springt.
 *
 * ============================================================================
 * MIT `schliesskreuz`: EINE FLÄCHE MIT INHALT (Auftrag CRM-102)
 * ============================================================================
 *
 * Jedes Pop-up schließt mit einem X — aber ein Auswahlmenü ist keines, das man
 * schließen müsste: Die Wahl schließt es, und wer nichts wählt, geht mit dem
 * Zeiger weg. Das Kreuz bekommt deshalb nur eine Fläche, die man liest, bevor
 * man etwas tut (die Glocke mit den eigenen fälligen Einträgen, „Alle
 * Aufgaben" in „Das steht an"). Es steht oben
 * rechts, im Quelltext aber hinter den Einträgen: Tab und der Fokus beim
 * Öffnen gehen zuerst auf den ersten Eintrag, und die Pfeiltasten übergehen
 * es — es ist kein Eintrag.
 */
export function Aufklapper({
  beschriftung,
  knopf,
  eintraege,
  kopf,
  leer,
  klasse,
  knopfklasse,
  aktuell,
  rechtsbuendig = false,
  kennung,
  zeile,
  verweis,
  schliesskreuz = false,
}: {
  /** Der Name des Knopfs für Vorlesehilfen und als Titel der Liste. */
  readonly beschriftung: string;
  /** Was im Knopf steht. Ohne Angabe die Beschriftung. */
  readonly knopf?: ReactNode;
  readonly eintraege: readonly Aufklappeintrag[];
  /** Eine Zeile über den Einträgen — etwa „3 offene Aufgaben". */
  readonly kopf?: ReactNode;
  /** Was dasteht, wenn es keine Einträge gibt. */
  readonly leer?: string;
  readonly klasse?: string;
  readonly knopfklasse?: string;
  /**
   * Der Knopf steht für den Ort, an dem man ist — die Gruppe der aktuellen
   * Maske. `page`, wenn er selbst die Seite ist; `true`, wenn die Seite unter
   * ihm liegt (WCAG 4.1.2: genau EINE Stelle sagt „page").
   */
  readonly aktuell?: 'page' | 'true' | undefined;
  /** Die Liste öffnet sich nach links, bündig mit dem rechten Rand des Knopfs. */
  readonly rechtsbuendig?: boolean;
  /** Eine feste Kennung für den Knopf — für `aria-labelledby` von außen. */
  readonly kennung?: string;
  /** Die Zeile, in der er beim Überfahren öffnet. Ohne Angabe nur beim Klick. */
  readonly zeile?: Schwebezeile;
  /**
   * Die Gruppe ist selbst ein Verweis (Auftrag #25): `inhalt` steht im Verweis,
   * `knopf` im Pfeilknopf daneben, der die Liste öffnet. `aktuell` gilt dann
   * dem Verweis. `beiWahl` bedient den gewöhnlichen Klick ohne Neuladen.
   */
  readonly verweis?: {
    readonly href: string;
    readonly inhalt: ReactNode;
    readonly beiWahl: () => void;
  };
  /** Ein Kreuz oben rechts in der Liste — nur für Flächen mit Inhalt (Auftrag CRM-102). */
  readonly schliesskreuz?: boolean;
}): JSX.Element {
  const [offen, setzeOffen] = useState(false);
  /* Ein Finger oder Stift hat die Liste geöffnet (Auftrag CRM-148). */
  const [perFinger, setzePerFinger] = useState(false);
  const feinerZeiger = useFeinerZeiger();
  const sichtbar = eintraege.filter((e) => e.nurFuerFinger !== true || perFinger || !feinerZeiger);
  const [lage, setzeLage] = useState<{ readonly oben: number; readonly seite: number }>({
    oben: 0,
    seite: 0,
  });
  const eigene = useId();
  const knopfId = kennung ?? `aufklapper-${eigene}`;
  const listenId = `${knopfId}-liste`;
  const huelle = useRef<HTMLDivElement>(null);
  const knopfRef = useRef<HTMLButtonElement>(null);
  const verweisRef = useRef<HTMLAnchorElement>(null);
  /** Wohin Escape den Fokus zurückgibt: dorthin, woher die Liste kam. */
  const ausloeser = useRef<HTMLElement | null>(null);
  const liste = useRef<HTMLDivElement>(null);

  /* Nur die Einträge — das Kreuz der Kopfzeile ist keiner (Auftrag CRM-102). */
  const eintragsknoepfe = (): HTMLButtonElement[] =>
    liste.current === null
      ? []
      : [...liste.current.querySelectorAll<HTMLButtonElement>('button.aufklapper__eintrag')];

  /* Geöffnet mit Klick oder Taste — dann schließt ihn das Verlassen nicht. */
  const angeheftet = useRef(false);
  /*
   * Womit der Verweis zuletzt gedrückt wurde (ÄA-68). Gemerkt beim Drücken,
   * gelesen beim Klick: Nicht jeder Browser meldet den Klick selbst als
   * `PointerEvent` mit `pointerType`. Eine Taste löscht es — Enter ist der Verweis.
   */
  const zeigerart = useRef('');
  /* Mit der Tastatur geöffnet: Der Fokus soll auf den ersten Eintrag, sobald es ihn gibt. */
  const fokusAufErsten = useRef(false);

  /*
   * DAS SCHLIESSEN VON AUSSEN. Beständig über die Lebenszeit, damit die Zeile
   * an der Identität erkennt, ob sie sich selbst meint. Es fasst nur Setzer,
   * Refs und die beständige Zeile an und darf deshalb die erste Fassung
   * behalten.
   */
  const zuVonAussen = useRef<(nurSchwebend: boolean) => void>(() => undefined);

  const schwebe = useSchwebe({
    oeffnen: () => {
      if (!offen) oeffnen(false, false);
    },
    schliessen: () => {
      if (!angeheftet.current) schliessen(false);
    },
  });

  zuVonAussen.current = (nurSchwebend: boolean): void => {
    if (nurSchwebend && angeheftet.current) return;
    schwebe.anhalten();
    angeheftet.current = false;
    fokusAufErsten.current = false;
    /* Schließt ein Punkt ohne Pfeil, trägt sich danach keiner ein: Die Zeile ist zu. */
    if (zeile !== undefined && zeile.offen === eigenesZu.current) zeile.offen = null;
    setzeOffen(false);
  };
  const eigenesZu = useRef<(nurSchwebend?: boolean) => void>((nurSchwebend = false) =>
    zuVonAussen.current(nurSchwebend),
  );

  /* Geht er weg, solange er offen ist, darf die Zeile ihn nicht mehr rufen. */
  useEffect(
    () => () => {
      if (zeile !== undefined && zeile.offen === eigenesZu.current) zeile.offen = null;
    },
    [zeile],
  );

  /* Bei einem geteilten Eintrag steht die Liste unter beiden, bündig mit dem Verweis. */
  function lageMessen(): void {
    const r = (verweis === undefined ? knopfRef.current : huelle.current)?.getBoundingClientRect();
    if (r === undefined) return;
    setzeLage({
      oben: r.bottom + 4,
      seite: rechtsbuendig ? Math.max(8, window.innerWidth - r.right) : Math.max(8, r.left),
    });
  }

  /*
   * DIE LISTE BLEIBT IM BILD (Auftrag CRM-105). Eine links bündige Liste an
   * einem Knopf weit rechts ragte auf dem Telefon über den Rand und schob die
   * Seite; nach unten reichte sie mit `100vh - 120px` unter die Adressleiste.
   * Gemessen wird nach dem Zeichnen, wenn die Breite feststeht — wie in
   * Segmente.tsx. Auf dem Desktop greift keine der beiden Grenzen, solange
   * die Liste ohnehin passt.
   */
  useLayoutEffect(() => {
    const l = liste.current;
    if (!offen || l === null) return;
    const hoehe = Math.min(window.innerHeight - 120, window.innerHeight - lage.oben - 8);
    l.style.maxHeight = `${String(Math.max(120, hoehe))}px`;
    if (!rechtsbuendig && lage.seite + l.offsetWidth > window.innerWidth - 8) {
      l.style.left = `${String(Math.max(8, window.innerWidth - 8 - l.offsetWidth))}px`;
    }
  }, [offen, lage, rechtsbuendig]);

  function oeffnen(mitFokus: boolean, anheften = true, finger = false): void {
    schwebe.anhalten();
    angeheftet.current = anheften;
    if (!offen) setzePerFinger(finger);
    if (zeile !== undefined) {
      /* Höchstens einer je Zeile offen: Der vorige schließt sofort. */
      if (zeile.offen !== null && zeile.offen !== eigenesZu.current) zeile.offen();
      zeile.offen = eigenesZu.current;
    }
    lageMessen();
    setzeOffen(true);
    /* Steht die Liste schon, sofort; sonst im Effekt gleich nach dem Zeichnen. */
    fokusAufErsten.current = mitFokus && !offen;
    if (mitFokus && offen) eintragsknoepfe()[0]?.focus();
  }

  /*
   * DER FOKUS OHNE VERZUG (Nachbesserung zu Auftrag CRM-42). Bis zum
   * 04.10.2026 wartete er auf den naechsten Frame (`requestAnimationFrame`).
   * In dieser Luecke ging eine schnelle Taste noch an den Knopf, und auf den
   * langsameren CI-Laeufern stand der Fokus noch nicht, als die Pruefung ihn
   * las. Der Effekt laeuft gleich nach dem Uebernehmen ins DOM: Die Liste
   * steht dann, und kein Frame liegt dazwischen.
   */
  useEffect(() => {
    if (!offen || !fokusAufErsten.current) return;
    fokusAufErsten.current = false;
    eintragsknoepfe()[0]?.focus();
  }, [offen]);

  function schliessen(fokusZurueck: boolean): void {
    schwebe.anhalten();
    angeheftet.current = false;
    fokusAufErsten.current = false;
    if (zeile !== undefined && zeile.offen === eigenesZu.current) zeile.offen = null;
    setzeOffen(false);
    if (fokusZurueck) (ausloeser.current ?? knopfRef.current)?.focus();
  }

  /* Daneben geklickt: zu. Die Liste liegt in der Hülle, der Klick darauf zählt als drinnen. */
  useEffect(() => {
    if (!offen) return;
    const beiDruck = (e: PointerEvent): void => {
      if (huelle.current !== null && !huelle.current.contains(e.target as Node)) schliessen(false);
    };
    const beiGroesse = (): void => schliessen(false);
    /*
     * ROLLT DIE SEITE, FOLGT DIE LISTE ihrem Knopf (Auftrag CRM-105) — sie
     * steht `fixed` und blieb sonst stehen, während der Knopf wegrollte. Das
     * Rollen in der Liste selbst zählt nicht.
     */
    const beiRollen = (e: Event): void => {
      if (liste.current !== null && e.target instanceof Node && liste.current.contains(e.target)) return;
      lageMessen();
    };
    /*
     * ESCAPE AUCH OHNE FOKUS (WCAG 1.4.13). Vom Überfahren geöffnet, steht der
     * Fokus nicht im Aufklapper, und dessen eigene Tastenfänger hören nichts.
     * Was Knopf oder Liste schon genommen haben, ist `defaultPrevented`.
     */
    const beiTaste = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      schliessen(false);
    };
    /* `pointerdown` statt `mousedown`: Safari auf dem Telefon meldet ein Tippen daneben sonst nicht. */
    document.addEventListener('pointerdown', beiDruck);
    window.addEventListener('resize', beiGroesse);
    window.addEventListener('scroll', beiRollen, true);
    window.addEventListener('keydown', beiTaste);
    return () => {
      document.removeEventListener('pointerdown', beiDruck);
      window.removeEventListener('resize', beiGroesse);
      window.removeEventListener('scroll', beiRollen, true);
      window.removeEventListener('keydown', beiTaste);
    };
  }, [offen]);

  /** Klick auf den Pfeil, Tippen auf den Verweis: öffnen, anheften, schließen. */
  function umschalten(von: HTMLElement | null, finger = false): void {
    ausloeser.current = von;
    /* Vom Überfahren geöffnet: Der Klick heftet an und schließt nicht (Auftrag #21). */
    if (!offen) oeffnen(false, true, finger);
    else if (!angeheftet.current) angeheftet.current = true;
    else schliessen(false);
  }

  function beiTasteImKnopf(e: ReactKeyboardEvent<HTMLElement>): void {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      ausloeser.current = e.currentTarget;
      oeffnen(true);
    } else if (e.key === 'Escape' && offen) {
      e.preventDefault();
      schliessen(true);
    }
  }

  function beiTasteInListe(e: ReactKeyboardEvent<HTMLDivElement>): void {
    const knoepfe = eintragsknoepfe();
    const stelle = knoepfe.indexOf(document.activeElement as HTMLButtonElement);
    const ziel = (i: number): void => {
      e.preventDefault();
      knoepfe[(i + knoepfe.length) % knoepfe.length]?.focus();
    };
    if (e.key === 'ArrowDown') ziel(stelle + 1);
    else if (e.key === 'ArrowUp') ziel(stelle - 1);
    else if (e.key === 'Home') ziel(0);
    else if (e.key === 'End') ziel(knoepfe.length - 1);
    else if (e.key === 'Escape') {
      e.preventDefault();
      /* Escape gehört dem Aufklapper und nicht dem Dialog dahinter. */
      e.stopPropagation();
      schliessen(true);
    }
  }

  return (
    <div
      className={klasse === undefined ? 'aufklapper' : `aufklapper ${klasse}`}
      ref={huelle}
      onBlur={(e) => {
        /* Mit Tab hinaus: zu, ohne den Fokus zurückzuholen. */
        if (offen && huelle.current !== null && !huelle.current.contains(e.relatedTarget as Node)) {
          schliessen(false);
        }
      }}
      {...(zeile === undefined
        ? {}
        : {
            /*
              ÜBERFAHREN NUR MIT DER MAUS (NFA-07, Auftrag #21). Ist in der
              Zeile schon einer offen, wechselt es sofort.
            */
            onPointerEnter: (e: ReactPointerEvent<HTMLDivElement>) =>
              schwebe.eintritt(e, offen || (zeile.offen !== null && zeile.offen !== eigenesZu.current)),
            onPointerLeave: (e: ReactPointerEvent<HTMLDivElement>) => schwebe.austritt(e),
          })}
    >
      {verweis !== undefined && (
        <a
          ref={verweisRef}
          id={knopfId}
          href={verweis.href}
          className={knopfklasse}
          aria-current={aktuell}
          onPointerDown={(e) => {
            zeigerart.current = e.pointerType;
          }}
          onClick={(e: ReactMouseEvent<HTMLAnchorElement>) => {
            const art = zeigerart.current;
            zeigerart.current = '';
            /* Neuer Tab, neues Fenster: Das tut der Browser selbst. */
            if (e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
            e.preventDefault();
            /* Finger und Stift schalten die Liste; darin steht „Übersicht" (ÄA-68, CRM-148). */
            if (art === 'touch' || art === 'pen') {
              umschalten(verweisRef.current, true);
              return;
            }
            schliessen(false);
            verweis.beiWahl();
          }}
          onKeyDown={(e) => {
            zeigerart.current = '';
            beiTasteImKnopf(e);
          }}
        >
          {verweis.inhalt}
        </a>
      )}
      <button
        ref={knopfRef}
        id={verweis === undefined ? knopfId : `${knopfId}-liste-knopf`}
        type="button"
        className={
          verweis !== undefined
            ? 'aufklapper__knopf aufklapper__schalter'
            : knopfklasse === undefined
              ? 'aufklapper__knopf'
              : `aufklapper__knopf ${knopfklasse}`
        }
        aria-expanded={offen}
        aria-controls={listenId}
        {...(verweis === undefined ? { 'aria-current': aktuell } : {})}
        {...(verweis !== undefined
          ? { 'aria-label': `Punkte von ${beschriftung}`, title: `Punkte von ${beschriftung}` }
          : knopf === undefined
            ? {}
            : { 'aria-label': beschriftung, title: beschriftung })}
        onClick={() => umschalten(knopfRef.current)}
        onKeyDown={beiTasteImKnopf}
      >
        {knopf ?? beschriftung}
      </button>
      {offen && (
        <div
          id={listenId}
          ref={liste}
          className={schliesskreuz ? 'aufklapper__liste aufklapper__liste--schliessbar' : 'aufklapper__liste'}
          style={
            rechtsbuendig
              ? { top: `${lage.oben}px`, right: `${lage.seite}px` }
              : { top: `${lage.oben}px`, left: `${lage.seite}px` }
          }
          onKeyDown={beiTasteInListe}
        >
          {kopf !== undefined && <div className="aufklapper__kopf">{kopf}</div>}
          {sichtbar.length === 0 ? (
            <p className="aufklapper__leer">{leer ?? 'Nichts da.'}</p>
          ) : (
            <ul aria-labelledby={knopfId}>
              {sichtbar.map((e) => (
                <li key={e.schluessel ?? e.text} {...(e.abgesetzt === true ? { className: 'aufklapper__abgesetzt' } : {})}>
                  <button
                    type="button"
                    className={`aufklapper__eintrag${e.aktuell === true ? ' aufklapper__eintrag--hier' : ''}`}
                    aria-current={e.aktuell === true ? 'page' : undefined}
                    {...(e.vorlesen === undefined ? {} : { 'aria-label': e.vorlesen })}
                    onClick={() => {
                      schliessen(false);
                      e.beiWahl();
                    }}
                  >
                    {e.vorne}
                    {e.unterzeile === undefined ? (
                      <span className="aufklapper__text">{e.text}</span>
                    ) : (
                      <span className="aufklapper__text">
                        {e.text}
                        <span className="aufklapper__unterzeile">{e.unterzeile}</span>
                      </span>
                    )}
                    {e.zusatz !== undefined && <span className="aufklapper__zusatz">{e.zusatz}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          {schliesskreuz && <Schliesskreuz beiKlick={() => schliessen(true)} />}
        </div>
      )}
    </div>
  );
}
